import React, { useState, useEffect } from 'react';
import { Code, Link as LinkIcon, Plus, Edit3, Trash2, Save, X } from 'lucide-react';
import { getQuickLinks, saveQuickLinks } from '../../services/homepageContentService';

// Icon mapping for dynamic icon rendering
const iconMap = {
  Code,
  Link: LinkIcon,
};

const iconOptions = [
  { value: 'Code', label: 'Code', Icon: Code },
  { value: 'Link', label: 'Link', Icon: LinkIcon },
];

/** Legacy seed links removed from the public sidebar */
const REMOVED_QUICK_LINK_LABELS = new Set([
  'My GitHub Profile',
  'Professional Portfolio',
]);

const filterQuickLinks = (links) =>
  (links || []).filter((link) => !REMOVED_QUICK_LINK_LABELS.has(link.label));

const QuickLinks = ({ isAdmin, setMessage }) => {
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  useEffect(() => {
    const loadQuickLinks = async () => {
      try {
        const data = await getQuickLinks();
        setQuickLinks(filterQuickLinks(data));
      } catch (error) {
        console.error('Error loading quick links:', error);
        setQuickLinks([]);
      } finally {
        setLoading(false);
      }
    };
    loadQuickLinks();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      // Filter out temporary IDs and ensure all required fields are present
      const linksToSave = quickLinks
        .filter(link => link.label && link.label.trim() !== '')
        .map(link => ({
          label: link.label.trim(),
          url: link.url || '#',
          icon: link.icon || 'Code',
        }));
      
      await saveQuickLinks(linksToSave);
      
      // Reload links from database to get proper IDs
      const data = await getQuickLinks();
      setQuickLinks(filterQuickLinks(data));
      
      setMessage("Quick links updated successfully!");
      setEditingLink(null);
      setIsAddingNew(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving quick links:', error);
      setMessage("Failed to save quick links. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isAddingNew) {
      // Remove the newly added link if canceling
      setQuickLinks(quickLinks.filter(link => link.id !== editingLink));
    }
    setEditingLink(null);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    const newLink = {
      id: `temp-${Date.now()}`,
      label: '',
      url: '',
      icon: 'Code',
      display_order: quickLinks.length + 1,
    };
    setQuickLinks([...quickLinks, newLink]);
    setIsAddingNew(true);
    setEditingLink(newLink.id);
  };

  const handleDelete = (linkId) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      setQuickLinks(quickLinks.filter(link => link.id !== linkId));
    }
  };

  const handleInputChange = (linkId, field, value) => {
    setQuickLinks(quickLinks.map(link =>
      link.id === linkId ? { ...link, [field]: value } : link
    ));
  };

  if (loading && quickLinks.length === 0) {
    return (
      <div className="p-6 panel">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin && quickLinks.length === 0) {
    return null;
  }

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="p-6 panel space-y-3">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 mb-3">Quick Links</h3>
        {quickLinks.map((link) => {
          const IconComponent = iconMap[link.icon] || Code;
          return (
            <a
              key={link.id}
              href={link.url}
              target={link.url.startsWith('http') ? '_blank' : undefined}
              rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 transition"
            >
              <IconComponent className="w-5 h-5" />
              <span>{link.label}</span>
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="p-6 panel space-y-3">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2">Quick Links</h3>
        <div className="flex space-x-2">
          {editingLink && (
            <button
              onClick={handleSave}
              disabled={loading || quickLinks.some(link => link.id === editingLink && !link.label.trim())}
              className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save All'}</span>
            </button>
          )}
          {!isAddingNew && !editingLink && (
            <button
              onClick={handleAddNew}
              className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
            >
              <Plus className="w-4 h-4" />
              <span>Add</span>
            </button>
          )}
        </div>
      </div>

      {quickLinks.map((link) => {
        const IconComponent = iconMap[link.icon] || Code;
        const isEditing = editingLink === link.id;

        if (isEditing) {
          return (
            <div key={link.id} className="p-4 border border-gray-300 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => handleInputChange(link.id, 'label', e.target.value)}
                  className="field"
                  placeholder="Link label"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => handleInputChange(link.id, 'url', e.target.value)}
                  className="field"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                <select
                  value={link.icon}
                  onChange={(e) => handleInputChange(link.id, 'icon', e.target.value)}
                  className="field"
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 px-3 py-1 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          );
        }

        return (
          <div key={link.id} className="flex items-center justify-between group">
            <a
              href={link.url}
              target={link.url.startsWith('http') ? '_blank' : undefined}
              rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 transition flex-1"
            >
              <IconComponent className="w-5 h-5" />
              <span>{link.label}</span>
            </a>
            <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition">
              <button
                onClick={() => setEditingLink(link.id)}
                className="p-1 text-indigo-600 hover:text-indigo-800 transition"
                title="Edit"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(link.id)}
                className="p-1 text-red-600 hover:text-red-800 transition"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}

      {quickLinks.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">No quick links yet. Click "Add" to create one.</p>
      )}
    </div>
  );
};

export default QuickLinks;
